import { resolveLocale } from '@frontends/util-i18n';
import { usePreferences } from '@frontends/util-preferences';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useEffectEvent } from 'react';
import { useLocation, useNavigate } from 'react-router';
import useAuth from '@/hooks/useAuth.tsx';
import { useEntityConfig } from '@/hooks/useEntityConfig.tsx';
import { crmOriginOf, embedEntryPath, readCrmMessage, themeModeOf } from '@/utils/b2coreEmbed.ts';

const B2CoreEmbedBridge = (): null => {
  const config = useEntityConfig();
  const crmOrigin = crmOriginOf(config.VITE_CLIENT_PORTAL_URL);
  const { setThemeMode, setLanguage } = usePreferences();
  const { token, setToken, setClient } = useAuth();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const location = useLocation();

  const logout = (accessToken: string): void => {
    fetch(`${config.VITE_URL_ID_BACKEND}/api/v1/auth/logout`, {
      method: 'DELETE',
      keepalive: true,
      credentials: 'include',
      headers: { Authorization: `Bearer ${accessToken}` },
    }).catch(() => undefined);

    queryClient.clear();
    setToken(undefined);
    setClient(undefined);
    void navigate(embedEntryPath(location.pathname + location.search), { replace: true });
  };

  const onCrmMessage = useEffectEvent((event: MessageEvent) => {
    const message = readCrmMessage(event, crmOrigin);

    if (message?.type === 'embed-theme-change') {
      const mode = themeModeOf(message.theme);

      if (mode !== null) {
        setThemeMode(mode);
      }
    }

    if (message?.type === 'embed-language-change') {
      const locale = resolveLocale(message.lang);

      if (locale !== null) {
        setLanguage(locale.language);
      }
    }

    if (message?.type === 'embed-logout' && token) {
      logout(token);
    }
  });

  useEffect(() => {
    if (crmOrigin === null || window.parent === window) {
      return;
    }

    const handleMessage = (event: MessageEvent): void => onCrmMessage(event);

    window.addEventListener('message', handleMessage);

    return () => window.removeEventListener('message', handleMessage);
  }, [crmOrigin]);

  return null;
};

export default B2CoreEmbedBridge;
